// Contractor label mapping - the "map contractors after the schedule is
// created" step. Imported schedule rows and issues keep the shorthand a
// document used for a party ("BES", "DP", "GG", "SpaceX"); here each label
// is resolved to a company on the project (owner or partner) and optionally
// a person, and the resolution is applied to everything carrying the label
// (now and on future imports).
//
// Used inside the import wizard (step 3) and from the coordination landing
// / schedule page (ContractorMappingModal).
import { usePartnerTrades } from "components/Partners/partnerTrades";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Popover, Select, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import { ReloadOutlined, SaveOutlined, TeamOutlined, UserAddOutlined } from "@ant-design/icons";
import { getContractorLabels, saveContractorLabels, createPartnerFromLabel } from "api/scheduleImport/scheduleImportApi";
import { matchOption } from "components/SearchSelect/searchSelect";

export default function ContractorMappingTable({ projectId, canEdit = true, compact = false, highlightLabels = [], onChanged, onLocalChange }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [labels, setLabels] = useState([]);
  const [choices, setChoices] = useState([]);
  const [draft, setDraft] = useState({});     // label -> { companyId, userId }
  const [force, setForce] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true); setError("");
    try {
      const r = await getContractorLabels(projectId);
      setLabels(r.labels || []);
      setChoices(r.choices || []);
      setDraft({});
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const seen = new Set(labels.map((l) => l.label));
    const extra = (highlightLabels || []).filter((l) => l && !seen.has(String(l).toUpperCase())).map((l) => ({ label: String(l).toUpperCase(), displayLabel: l, items: 0, issues: 0, contacts: [], pending: true }));
    return [...labels, ...extra];
  }, [labels, highlightLabels]);

  // Each company with its trades beneath (owner, 2026-10-16); searched and
  // shown closed by `text`.
  const tradeIx = usePartnerTrades(projectId);
  const companyOptions = choices.map((c) => {
    const name = `${c.companyName}${c.isOwner ? " (owner)" : ""}`;
    const trades = c.isOwner ? [] : tradeIx.tradesOf(c.companyId);
    return { value: c.companyId, text: [name, ...trades].join(" "), name, label: <PartnerWithTrades name={name} trades={trades} /> };
  });
  // People for a company: its accounts, then its project-roster contacts
  // and the contacts the documents listed under this label. Contacts with
  // an email but no account yet are offered as "email:<address>" - saving
  // the mapping creates their (magic-login) account and links the roster,
  // exactly like assigning an issue to them.
  const userOptionsFor = (companyId, row) => {
    const c = choices.find((x) => x.companyId === companyId);
    if (!c) return [];
    const opts = (c.users || []).map((u) => ({ value: u.userId, label: `${u.name} <${u.email}>`, email: String(u.email || "").toLowerCase() }));
    const seen = new Set(opts.map((o) => o.email).filter(Boolean));
    const addContact = (r, from) => {
      const email = String(r.email || "").toLowerCase().trim();
      if (r.userId && !opts.some((o) => o.value === r.userId)) { opts.push({ value: r.userId, label: `${r.name || email} <${email}>`, email }); seen.add(email); return; }
      if (!r.userId && email && !seen.has(email)) { opts.push({ value: `email:${email}`, label: `${r.name || email} <${email}> · no account yet (${from})`, email }); seen.add(email); }
    };
    for (const r of c.roster || []) addContact(r, "roster");
    for (const r of (row && row.contacts) || []) addContact(r, "from documents");
    return opts;
  };

  const setLocal = (label, patch) => {
    setDraft((d) => {
      const next = { ...d, [label]: { ...(d[label] || {}), ...patch } };
      onLocalChange && onLocalChange(next);
      return next;
    });
  };
  const valueOf = (row, key) => (draft[row.label] && draft[row.label][key] !== undefined ? draft[row.label][key] : row[key] || undefined);

  const save = async () => {
    const entries = Object.entries(draft).map(([label, v]) => ({ label, companyId: v.companyId || "", userId: v.userId || "", userName: v.userName || undefined })).filter((e) => e.companyId || e.userId);
    if (!entries.length) { message.info("Nothing to save - pick a company for a label first"); return; }
    setSaving(true);
    try {
      const r = await saveContractorLabels(projectId, entries, force);
      const applied = (r.results || []).reduce((n, x) => n + (x.applied?.items || 0) + (x.applied?.issues || 0), 0);
      const failed = (r.results || []).filter((x) => x.error);
      if (failed.length) message.warning(`${failed.map((x) => `${x.label}: ${x.error}`).join("; ")}`);
      message.success(`Mapping saved - applied to ${applied} schedule row(s) / issue(s)`);
      setLabels(r.labels || []); setDraft({});
      onChanged && onChanged(r.labels || []);
    } catch (e) { message.error(e.message); }
    finally { setSaving(false); }
  };

  const createPartner = async (row) => {
    try {
      const r = await createPartnerFromLabel(projectId, row.label, {});
      message.success(`Partner company "${r.companyName}" created and mapped to ${row.displayLabel || row.label}`);
      setLabels(r.labels || []);
      onChanged && onChanged(r.labels || []);
    } catch (e) { message.error(e.message); }
  };

  const columns = [
    {
      title: "Label in documents", dataIndex: "label", key: "label", width: 200,
      render: (v, row) => (
        <Space size={4} wrap>
          <Tag color={row.companyId ? "green" : row.pending ? "blue" : "gold"}>{row.displayLabel || v}</Tag>
          {highlightLabels.includes(v) && <Tag color="blue">in this file</Tag>}
        </Space>
      ),
    },
    {
      title: "Used by", key: "used", width: 150,
      render: (_, r) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {r.items || 0} schedule row{r.items === 1 ? "" : "s"}, {r.issues || 0} issue{r.issues === 1 ? "" : "s"}
          {(r.unresolvedItems || r.unresolvedIssues) ? <><br />{(r.unresolvedItems || 0) + (r.unresolvedIssues || 0)} unassigned</> : null}
        </Typography.Text>
      ),
    },
    {
      title: "Contacts seen", key: "contacts", width: 170,
      render: (_, r) => r.contacts && r.contacts.length ? (
        <Popover title={`Contacts under ${r.displayLabel || r.label}`} content={
          <div style={{ maxWidth: 360 }}>
            {r.contacts.map((c, i) => (
              <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                <b>{c.name}</b>{c.role ? ` - ${c.role}` : ""}{c.email ? <><br />{c.email}</> : null}{c.phone ? ` · ${c.phone}` : ""}{c.region ? ` · ${c.region}` : ""}
              </div>
            ))}
          </div>
        }>
          <a><TeamOutlined /> {r.contacts.length} contact{r.contacts.length === 1 ? "" : "s"}</a>
        </Popover>
      ) : <Typography.Text type="secondary" style={{ fontSize: 12 }}>-</Typography.Text>,
    },
    {
      title: "Company on the project", key: "company", width: 260,
      render: (_, r) => (
        <Select filterOption={matchOption}
          size="small" style={{ width: "100%" }} allowClear showSearch optionFilterProp="text" optionLabelProp="name"
          placeholder="Owner or partner company"
          disabled={!canEdit}
          value={valueOf(r, "companyId") || undefined}
          options={companyOptions}
          onChange={(v) => setLocal(r.label, { companyId: v || "", userId: "" })}
        />
      ),
    },
    {
      title: "Person (optional)", key: "user", width: 240,
      render: (_, r) => (
        <Select filterOption={matchOption}
          size="small" style={{ width: "100%" }} allowClear showSearch optionFilterProp="label"
          placeholder="Responsible person"
          disabled={!canEdit || !valueOf(r, "companyId")}
          value={valueOf(r, "userId") || undefined}
          options={userOptionsFor(valueOf(r, "companyId"), r)}
          onChange={(v, opt) => setLocal(r.label, { userId: v || "", userName: v && String(v).startsWith("email:") ? String((opt && opt.label) || "").split(" <")[0] : undefined })}
        />
      ),
    },
    {
      title: "", key: "actions", width: 170,
      render: (_, r) => (!r.companyId && canEdit && r.contacts && r.contacts.length && !r.pending) ? (
        <Tooltip title="Create a partner company named after this label with these contacts on its roster, and map the label to it. No accounts are created and nothing is emailed.">
          <Button size="small" icon={<UserAddOutlined />} onClick={() => createPartner(r)}>Create partner</Button>
        </Tooltip>
      ) : null,
    },
  ];

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
      {!compact && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          Documents name parties by shorthand. Map each label to a company (and optionally a person) once - every schedule row and issue
          carrying the label gets that responsible party, and later imports apply the same mapping automatically.
        </Typography.Paragraph>
      )}
      <Table
        size="small" rowKey="label" loading={loading} columns={columns} dataSource={rows}
        pagination={rows.length > 12 ? { pageSize: 12, size: "small" } : false}
        locale={{ emptyText: "No contractor labels yet - import a document that names parties, or map contractors from the schedule." }}
      />
      {canEdit && (
        <Space style={{ marginTop: 8 }} wrap>
          <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={save} disabled={!Object.keys(draft).length}>
            Save mapping & apply
          </Button>
          <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)}>
            <span style={{ fontSize: 12 }}>Also overwrite rows that already have a responsible company</span>
          </Checkbox>
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Space>
      )}
    </div>
  );
}
